/**
 * auto-captions-progress — decides what the transcribe progress bar should show
 * while the model is still downloading.
 *
 * Why this is its own file: the decision is the whole point of a UX fix and it
 * needs a deterministic unit test. Driving the real ~39 MB download under a
 * network throttle is too flaky to assert on (real stalls, host failover, the
 * 30 s download watchdog all fire nondeterministically). A pure function does
 * not have that problem.
 *
 * The bug it fixes (measured on an emulated phone — 4x CPU, 4G, first visit,
 * 2026-07-24): the bar showed "Извлечение звука 0%" for ~98 seconds while the
 * weights streamed in unannounced, then jumped to "Загрузка AI-модели 46%".
 * Audio extraction is not what takes 98 s — the model download is, and it was
 * invisible. Prod telemetry puts abandonment at 60-180 s, exactly that window.
 *
 * Rule: while the model download owns the bar (in flight, not finished), the
 * bar reports the DOWNLOAD — its real, rising percentage under the "downloading
 * model" label — regardless of which pipeline stage is technically running.
 * Once the download is done (or never started, e.g. warm cache / desktop where
 * it finished before the click), the stage drives the bar as before.
 *
 * `decide()` is pure. Exposed as `window.__acProgress` (browser) +
 * module.exports (node tests).
 */
(function () {
    'use strict';

    // The slice of the overall bar the download occupies. Extraction/VAD sit at
    // the very start, recognition fills the rest — the download is the long
    // pole between them, so it owns 5%..50%.
    var DL_SPAN = [0.05, 0.50];

    function ownsBar(modelDlPct, loadDone) {
        var p = +modelDlPct || 0;
        return !loadDone && p > 0 && p < 99.5;
    }

    /**
     * @param req   {frac, label, pct} the stage WANTED to show
     * @param ctx   {modelDlPct, loadDone, downloadingLabel}
     * @returns     {frac, label, pct} what the bar should actually show
     */
    function decide(req, ctx) {
        ctx = ctx || {};
        var p = +ctx.modelDlPct || 0;
        if (ownsBar(p, ctx.loadDone)) {
            var frac = DL_SPAN[0] + (DL_SPAN[1] - DL_SPAN[0]) * (p / 100);
            return { frac: frac, label: ctx.downloadingLabel || req.label, pct: Math.round(p) };
        }
        return { frac: req.frac, label: req.label, pct: req.pct };
    }

    var api = { decide: decide, ownsBar: ownsBar, DL_SPAN: DL_SPAN };
    if (typeof window !== 'undefined') window.__acProgress = api;
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
})();
